import { defineStore } from "pinia";

const VOLUME_STORAGE_KEY = "celestia-ambiance-volume";
const DEFAULT_USER_VOLUME = 0.4;
const MOBILE_MAX_WIDTH = 768;

let bodyLockResizeBound = false;

function clampVolume(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_USER_VOLUME;
  return Math.min(1, Math.max(0, n));
}

function loadUserVolume() {
  try {
    const stored = localStorage.getItem(VOLUME_STORAGE_KEY);
    if (stored == null) return DEFAULT_USER_VOLUME;
    return clampVolume(stored);
  } catch {
    return DEFAULT_USER_VOLUME;
  }
}

function persistUserVolume(value) {
  try {
    localStorage.setItem(VOLUME_STORAGE_KEY, String(value));
  } catch {
    /* ignore quota / private mode */
  }
}

export const useUiStore = defineStore("ui", {
  state: () => ({
    isSplashScreenVisible: false,
    isAmbiancePlaying: false,
    userVolume: loadUserVolume(),
    audioElement: null,
    fadeIntervalId: null,
    sidePanelOpen: false,
    infoDrawerOpen: false,
    isEditingDefense: false,
  }),
  actions: {
    bindBodyLockResize() {
      if (bodyLockResizeBound || typeof window === "undefined") return;
      bodyLockResizeBound = true;
      window.addEventListener("resize", () => {
        this.syncBodyScrollLock();
      });
    },
    syncBodyScrollLock() {
      this.bindBodyLockResize();
      if (typeof document === "undefined") return;
      const mobile =
        typeof window !== "undefined" && window.innerWidth <= MOBILE_MAX_WIDTH;
      const anyDrawer = this.infoDrawerOpen || this.sidePanelOpen;
      document.body.style.overflow = mobile && anyDrawer ? "hidden" : "";
    },
    setSidePanelOpen(open) {
      this.sidePanelOpen = !!open;
      this.syncBodyScrollLock();
    },
    setInfoDrawerOpen(open) {
      this.infoDrawerOpen = !!open;
      this.syncBodyScrollLock();
    },
    setEditingDefense(open) {
      this.isEditingDefense = !!open;
    },
    showSplashScreen() {
      this.isSplashScreenVisible = true;
    },
    hideSplashScreen() {
      this.isSplashScreenVisible = false;
    },

    setAmbianceVolume(value) {
      this.userVolume = clampVolume(value);
      persistUserVolume(this.userVolume);
      this.clearFade();
      if (this.audioElement && this.isAmbiancePlaying) {
        this.audioElement.volume = this.userVolume;
      }
    },

    clearFade() {
      if (this.fadeIntervalId != null) {
        clearInterval(this.fadeIntervalId);
        this.fadeIntervalId = null;
      }
    },

    /**
     * Toggle ambiance music playback
     * @param {string} audioSrc - Path to the audio file
     */
    async toggleAmbiance(audioSrc) {
      try {
        if (this.isAmbiancePlaying) {
          this.stopAudio();
        } else {
          const existingSrc = this.audioElement?.src || "";
          const srcChanged =
            !!this.audioElement &&
            !!audioSrc &&
            !existingSrc.endsWith(audioSrc) &&
            existingSrc !== audioSrc;
          if (!this.audioElement || srcChanged) {
            if (this.audioElement) {
              this.audioElement.pause();
            }
            this.audioElement = new Audio(audioSrc);
            this.audioElement.volume = 0;

            this.audioElement.onended = () => {
              this.clearFade();
              this.isAmbiancePlaying = false;
            };
          }

          this.audioElement.currentTime = 0;
          await this.audioElement.play();
          this.isAmbiancePlaying = true;
          this.fadeInAudio();
        }
      } catch (error) {
        console.warn("Audio autoplay was blocked by the browser:", error);
        this.isAmbiancePlaying = false;
      }
    },

    /**
     * Fade in the audio volume from 0 to the user's level over 2 seconds
     */
    fadeInAudio() {
      if (!this.audioElement) return;

      this.clearFade();

      const fadeDuration = 2000;
      const fadeSteps = 20;
      const stepDuration = fadeDuration / fadeSteps;
      const targetVolume = this.userVolume;
      const volumeIncrement = targetVolume / fadeSteps;

      let currentStep = 0;
      this.audioElement.volume = 0;

      this.fadeIntervalId = setInterval(() => {
        if (currentStep >= fadeSteps || !this.isAmbiancePlaying) {
          this.clearFade();
          if (this.audioElement && this.isAmbiancePlaying) {
            this.audioElement.volume = this.userVolume;
          }
          return;
        }

        currentStep++;
        const newVolume = Math.min(currentStep * volumeIncrement, this.userVolume);
        if (this.audioElement) {
          this.audioElement.volume = newVolume;
        }
      }, stepDuration);
    },

    /**
     * Stop and reset the audio playback
     */
    stopAudio() {
      this.clearFade();
      if (this.audioElement) {
        this.audioElement.pause();
        this.audioElement.currentTime = 0;
        this.audioElement = null;
      }
      this.isAmbiancePlaying = false;
    },
  },
});
